import { AwsClient } from 'aws4fetch';
import { fetchTemporarySigV4Credentials } from '../../auth/midway';
import { getEnvironmentConfig } from '../../configurations/config';
import {
  MappingJob,
  MappingJobConfig,
} from '../../types/dynamodb';

export class MappingJobApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly path: string,
  ) {
    super(message);
    this.name = 'MappingJobApiError';
  }
}

/**
 * Thin SigV4 client for the NexusEngine mapping-jobs Lambda Function URL.
 * The function URL is authType AWS_IAM so requests must be signed with the
 * caller's Midway-federated credentials.
 */
export class NexusMappingJobService {
  private readonly baseUrl: string;
  private readonly region: string;

  constructor() {
    const config = getEnvironmentConfig();
    this.baseUrl = config.mappingJobsApiUrl;
    this.region = config.mappingJobsApiRegion;
  }

  /** Feature flag — the UI hides job-related widgets when false. */
  isEnabled(): boolean {
    return this.baseUrl.length > 0;
  }

  async listJobs(): Promise<MappingJob[]> {
    return this.send<MappingJob[]>('GET', '/v1/mapping-jobs');
  }

  async getJob(jobId: string): Promise<MappingJob | null> {
    try {
      return await this.send<MappingJob>(
        'GET',
        `/v1/mapping-jobs/${encodeURIComponent(jobId)}`,
      );
    } catch (err) {
      if (err instanceof MappingJobApiError && err.statusCode === 404) {
        return null;
      }
      throw err;
    }
  }

  async createJob(config: MappingJobConfig): Promise<MappingJob> {
    return this.send<MappingJob>('POST', '/v1/mapping-jobs', config);
  }

  private async send<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    if (!this.isEnabled()) {
      throw new MappingJobApiError(
        'Mapping jobs API is not configured for this stage',
        503,
        path,
      );
    }

    const midway = await fetchTemporarySigV4Credentials();
    if (!midway.AccessKeyId || !midway.SecretAccessKey) {
      throw new MappingJobApiError(
        'Failed to retrieve Midway credentials',
        401,
        path,
      );
    }

    const aws = new AwsClient({
      accessKeyId: midway.AccessKeyId,
      secretAccessKey: midway.SecretAccessKey,
      sessionToken: midway.SessionToken,
      region: this.region,
      service: 'lambda',
    });

    const url = new URL(path, this.baseUrl).toString();
    const init: RequestInit = { method, headers: {} };
    if (body !== undefined) {
      (init.headers as Record<string, string>)['Content-Type'] =
        'application/json';
      init.body = JSON.stringify(body);
    }

    const response = await aws.fetch(url, init);
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new MappingJobApiError(
        `Mapping jobs API ${method} ${path} failed: ${response.status} ${text}`,
        response.status,
        path,
      );
    }

    return (await response.json()) as T;
  }
}
